// The password bar: the portfolio's contact bar, reused.
// A circle scales in and flows straight into the bar; the label, the field and
// the arrow come in right to left. Wrong: the label turns red and asks again.
// Right: the fields leave in the same order, the bar closes back into its
// circle and the project comes in.
import * as React from "react"
import { Dots } from "../toolkit/Dots"
import { EASE, MS, enter, freeze, leave, reduced } from "../toolkit/motion"
import { followMove, login, studioLogin } from "./api"
import { setLang, t } from "./i18n"

type Status = "idle" | "checking" | "wrong" | "many" | "error"
const H = 75

export function Login({ client, onIn, studio }: { client: string; onIn: () => void; studio?: boolean }) {
    const bar = React.useRef<HTMLFormElement>(null)
    const field = React.useRef<HTMLInputElement>(null)
    const [password, setPassword] = React.useState("")
    const [status, setStatus] = React.useState<Status>("idle")
    const [leaving, setLeaving] = React.useState(false)

    // open: circle, then bar (overlapping, no pause)
    React.useLayoutEffect(() => {
        const el = bar.current!
        const w = el.offsetWidth
        const r = reduced()
        if (r) {
            el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 300, easing: "linear" })
        } else {
            el.animate([{ transform: "scale(0)", opacity: 0 }, { transform: "scale(1)", opacity: 1, offset: 0.66 }, { transform: "scale(1)", opacity: 1 }], {
                duration: 300,
                easing: EASE.out,
            })
            el.animate([{ width: `${H}px` }, { width: `${w}px` }], { duration: 900, delay: 30, easing: EASE.barOpen, fill: "backwards" })
        }
        // the parts in, right to left
        const parts = Array.from(el.querySelectorAll<HTMLElement>("[data-in]")).sort((a, b) => Number(a.dataset.in) - Number(b.dataset.in))
        parts.forEach((p, i) =>
            p.animate(
                [
                    { opacity: 0, transform: r ? "none" : `translateX(${p.dataset.dir === "ltr" ? -14 : 20}px)` },
                    { opacity: 1, transform: "none" },
                ],
                { duration: 700, delay: (r ? 0 : 560) + i * MS.stagger, easing: EASE.out, fill: "backwards" }
            )
        )
        const t = window.setTimeout(() => field.current?.focus({ preventScroll: true }), r ? 0 : 700)
        return () => window.clearTimeout(t)
    }, [])

    const submit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (status === "checking") return
        if (!password) {
            setStatus("wrong")
            return
        }
        setStatus("checking")
        const result = studio ? await studioLogin(password) : await login(client, password)
        if (result !== "ok") {
            setStatus(result)
            field.current?.select()
            return
        }
        // out: the same order they came in, then the bar closes into its circle
        setLeaving(true)
        const el = bar.current!
        field.current?.blur()
        const parts = Array.from(el.querySelectorAll<HTMLElement>("[data-in]")).sort((a, b) => Number(a.dataset.in) - Number(b.dataset.in))
        await leave(parts, { to: "left" })
        freeze(el)
        if (!reduced()) {
            const w = el.offsetWidth
            await el
                .animate([{ width: `${w}px`, opacity: 1 }, { width: `${H}px`, opacity: 1, offset: 0.75 }, { width: `${H}px`, opacity: 0, transform: "scale(.6)" }], {
                    duration: 700,
                    easing: EASE.natural,
                    fill: "forwards",
                })
                .finished.catch(() => {})
        }
        onIn()
    }

    const title =
        status === "wrong"
            ? t("Not quite, try again?")
            : status === "many"
              ? t("Too many tries, wait a few minutes")
              : status === "error"
                ? t("Couldn't check, try again?")
                : studio
                  ? "Studio"
                  : t("Client access")

    return (
        <main className="p-login">
            <div className="p-login-stack">
            {!studio && <Hello client={client} leaving={leaving} />}
            <form ref={bar} className="p-bar" onSubmit={submit} data-status={status} noValidate>
                <span className="p-bar-title" data-in="3">
                    {title}
                </span>
                <label className="p-bar-field" data-in="2">
                    <span className="p-bar-label">Password</span>
                    <input
                        ref={field}
                        type="password"
                        name="password"
                        autoComplete="current-password"
                        placeholder={studio ? "your password" : t("your password")}
                        value={password}
                        onChange={(e) => {
                            setPassword(e.target.value)
                            if (status !== "checking") setStatus("idle")
                        }}
                    />
                </label>
                <button className="p-send" type="submit" aria-label="Open" disabled={status === "checking"} data-in="1" data-dir="ltr">
                    <Dots shape="arrow" />
                </button>
            </form>
            </div>
        </main>
    )
}

/** whose page this is: their logo and name, small, above the bar */
function Hello({ client, leaving }: { client: string; leaving: boolean }) {
    const [who, setWho] = React.useState<{ name: string; logo: string | null; lang?: "en" | "nl" } | null>(null)
    const el = React.useRef<HTMLDivElement>(null)
    React.useEffect(() => {
        if (!client) return
        fetch(`/api/hello?client=${encodeURIComponent(client)}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((w) => {
                if (w?.moved) return void followMove(client)
                if (w) setLang(w.lang)
                setWho(w)
            })
            .catch(() => {})
    }, [client])
    React.useLayoutEffect(() => {
        if (who && el.current) enter(el.current, { from: "up", distance: 14, delay: 420, duration: 900 })
    }, [who])
    React.useEffect(() => {
        if (leaving && el.current) el.current.animate([{ opacity: 1 }, { opacity: 0, transform: "translateY(-10px)" }], { duration: 360, easing: EASE.exit, fill: "forwards" })
    }, [leaving])
    if (!who) return null
    return (
        <div ref={el} className="p-hello">
            {who.logo && <img src={who.logo} alt="" />}
            <span>{who.name}</span>
        </div>
    )
}
